/** RangeHistogramSlider.jsx */
import React, { useState, useEffect, useRef } from 'react';
import * as Slider from '@radix-ui/react-slider';
import RangeHistogram from './RangeHistogram';
import EditableThumbWithLabel from './EditableThumbWithLabel';
import MergedLabel from './MergedLabel';

export default function RangeHistogramSlider({
  label,
  min,
  max,
  histogramData,
  binSize,
  selectedRange, // [leftVal, rightVal]
  onValueChange,
  formatValue,
  valueKey,
}) {
  // Unpack (and correct order if needed)
  let [leftVal, rightVal] = selectedRange;
  if (leftVal > rightVal) {
    [leftVal, rightVal] = [rightVal, leftVal];
  }

  // Decide if we "merge" the labels (distance-based threshold)
  const mergeThreshold = binSize * 8; // or any logic you like
  const merged = (rightVal - leftVal) <= mergeThreshold;

  // Handler for slider dragging
  const handleSliderChange = (vals) => {
    // Radix calls onValueChange with array [leftVal, rightVal]
    // Ensure order
    let [l, r] = vals;
    if (l > r) [l, r] = [r, l];
    onValueChange([l, r]);
  };

  // Individual edits from each input bubble
  const handleLeftEdit = (newLeft) => {
    if (newLeft > rightVal) newLeft = rightVal;
    onValueChange([newLeft, rightVal]);
  };
  const handleRightEdit = (newRight) => {
    if (newRight < leftVal) newRight = leftVal;
    onValueChange([leftVal, newRight]);
  };

  // For merged label positioning, compute midpoint in % of track
  const range = max - min;
  const leftPercent = ((leftVal - min) / range) * 100;
  const rightPercent = ((rightVal - min) / range) * 100;
  const midpointPercent = (leftPercent + rightPercent) / 2;

  return (
    <div className="w-full mb-12 px-4">
      <div className="mb-2">
      <label className="block font-semibold -ml-11 mb-2 text-zinc-700 text-lg">{label}</label>
      </div>

      {/* 1) Histogram */}
      <div className="relative h-24 overflow-hidden ">
        <RangeHistogram
          data={histogramData}
          selectedRange={[leftVal, rightVal]}
          binSize={binSize}
          originalMin={min}
          originalMax={max}
          valueKey={valueKey}
        />
      </div>

      {/* 2) Slider */}
      <div className="relative -mt-[100px] ">
        <Slider.Root
        minStepsBetweenThumbs={1}
          value={[leftVal, rightVal]}
          onValueChange={handleSliderChange}
          min={min}
          max={max}
          step={binSize}
     className="relative flex items-center select-none w-full h-[150px]"
        >
          {/* Track */}
          <Slider.Track className="relative w-full h-full pointer-events-auto bg-transparent">
          <div className="absolute top-1/2 left-0 w-full h-[3px] -translate-y-[-21px] bg-zinc-100 pointer-events-none rounded-lg" />
           <Slider.Range className="absolute top-1/2 left-0 h-[3px] -translate-y-[-21px] bg-emerald-400 pointer-events-none" />
          </Slider.Track>

          {/* LEFT THUMB (asChild) */}
          <Slider.Thumb asChild className="block w-4 h-4 bg-white border border-emerald-400 shadow-md rounded-full -translate-y-[-22px]">
            <div className="pointer-events-auto ">
              <EditableThumbWithLabel
                value={leftVal}
                min={min}
                max={max}
                step={binSize}
                onChange={handleLeftEdit}
                hideLabel={merged}
                formatValue={formatValue}
              />
            </div>
          </Slider.Thumb>

          {/* RIGHT THUMB (asChild) */}
          <Slider.Thumb asChild className="block w-4 h-4 bg-white border border-emerald-400 shadow-md rounded-full -translate-y-[-22px]">
            <div className="pointer-events-auto">
              <EditableThumbWithLabel
                value={rightVal}
                min={min}
                max={max}
                step={binSize}
                onChange={handleRightEdit}
                hideLabel={merged}
                formatValue={formatValue}
              />
            </div>
          </Slider.Thumb>
        </Slider.Root>

        {/* 3) Merged label if thresholds met */}
        {merged && (
          <MergedLabel
            leftValue={leftVal}
            rightValue={rightVal}
            formatValue={formatValue}
            onEditLeft={handleLeftEdit}
            onEditRight={handleRightEdit}
            style={{
              position: 'absolute',
              top: '114px', // place below track
              left: `${midpointPercent}%`,
              transform: 'translateX(-50%)',
            }}
          />
        )}
      </div>
    </div>
  );
}